import {
  AlertCircle,
  AlertTriangle,
  CheckCircleBroken,
  InfoCircle,
} from '@untitled-ui/icons-react';
import { type ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

import { Button } from './buttons/button';
import { FeaturedIcon } from './featured-icon';
import { Loading } from './loading';

export enum ToastBannerVariant {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
  Loading = 'loading',
}

type ToastBannerProps = {
  title: string;
  description?: string;
  descriptionChild?: ReactNode;
  cta?: {
    text: string;
    loading?: boolean;
    onClick: () => unknown;
  };
  variant?: ToastBannerVariant;
  size?: 'sm' | 'md';
  className?: string;
  descriptionClassName?: string;
};

const ROOT_MD_CLASSES =
  'flex w-full items-center justify-between rounded-lg border py-6 px-5 shadow-toast bg-white gap-2';
const ROOT_SM_CLASSES =
  'flex w-full items-center justify-between rounded-lg border p-3 shadow-toast bg-white gap-2';

const TITLE_MD_CLASSES = 'font-body-1-sb truncate';
const TITLE_SM_CLASSES = 'font-body-2-sb truncate';

function ToastIcon(props: { variant: ToastBannerVariant; size: 'sm' | 'md' }) {
  if (props.variant === ToastBannerVariant.Loading) {
    return <Loading size="md" className="text-branding-accent" />;
  }
  if (props.variant === ToastBannerVariant.Info) {
    return <FeaturedIcon icon={<InfoCircle />} color="gray" size={props.size} />;
  }
  if (props.variant === ToastBannerVariant.Success) {
    return <FeaturedIcon icon={<CheckCircleBroken />} color="green" size={props.size} />;
  }
  if (props.variant === ToastBannerVariant.Warning) {
    return <FeaturedIcon icon={<AlertTriangle />} color="yellow" size={props.size} />;
  }

  return <FeaturedIcon icon={<AlertCircle />} color="red" size={props.size} />;
}

export function ToastBanner(props: ToastBannerProps) {
  const { cta, description, descriptionChild } = props;

  const rootClass = twMerge([
    props.size === 'sm' ? ROOT_SM_CLASSES : ROOT_MD_CLASSES,
    props.className ?? '',
  ]);
  const descClass = twMerge([
    'font-body-2-r line-clamp-3 overflow-hidden text-ellipsis text-dark-gray mt-1',
    props.descriptionClassName ?? '',
  ]);

  return (
    <div className={rootClass}>
      <div className="flex w-[300px] flex-1 items-center gap-4">
        <ToastIcon variant={props.variant ?? ToastBannerVariant.Info} size={props.size ?? 'md'} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className={props.size === 'sm' ? TITLE_SM_CLASSES : TITLE_MD_CLASSES}>
            {props.title}
          </div>
          {description != null && descriptionChild == null ? (
            <div className={descClass} dangerouslySetInnerHTML={{ __html: description }}></div>
          ) : null}
          {description == null && descriptionChild != null ? (
            <div className={descClass}>{descriptionChild}</div>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center justify-between gap-1">
        {cta != null ? (
          <Button variant="ghost-accent" size="sm" onClick={cta.onClick} loading={cta.loading}>
            {cta.text}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
